import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronDown } from "lucide-react";
import { useCallback, useLayoutEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { images } from "@/assets";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const DataTableCollapsibleRow = ({
  data,
  columns,
  className,
  onRowClick,
}: {
  data: any;
  columns: any;
  className?: string;
  onRowClick?: any;
}) => {
  const taskData = useSelector((state: RootState) => state.currentData.taskData);
  const [expanded, setExpanded] = useState({});

  const table = useReactTable({
    data,
    columns,
    state: { expanded },
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row: any) => row.children, // ⭐ IMPORTANT
  });

  // ⭐ FIXED: Auto-expand using stable row IDs
  useLayoutEffect(() => {
    if (taskData && data.length > 0) {
      expandToTask(table.getRowModel().flatRows, taskData.taskId || taskData?.id);
    }
  }, [taskData, data.length]);

  // ⭐ Helper function using stable taskId
  const expandToTask = useCallback((rows: any[], targetTaskId: string | number) => {    
    const newExpanded: Record<string, boolean> = {};
    
    const expandRowAndParents = (row: any) => {
      // Check if this row contains the target task
      const hasTargetTask = row.original.children?.some((child: any) => 
        child.taskId === targetTaskId || child.id === targetTaskId
      ) || row.original.taskId === targetTaskId || row.original.id === targetTaskId;

      if (hasTargetTask) {
        // Expand this row
        newExpanded[row.id] = true;
        
        // Expand all parents
        let parent = row.parentRow;
        while (parent) {
          newExpanded[parent.id] = true;
          parent = parent.parentRow;
        }
        return true;
      }
      
      // Recurse into children
      if (row.subRows && row.subRows.length > 0) {
        for (const child of row.subRows) {
          if (expandRowAndParents(child)) {
            newExpanded[row.id] = true;
            return true;
          }
        }
      }
      
      return false;
    };

    // Try all top-level rows
    for (const row of rows) {
      expandRowAndParents(row);
    }

    // console.log('📂 New expanded state:', newExpanded);
    setExpanded(newExpanded);
  }, []);

  return (
    <div className={cn("border border-gray-200 shadow-md overflow-hidden bg-white w-full", className)}>
      <div className="lg:w-full">
        <Table>
          {/* Main table header */}
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-primary-100 hover:bg-primary-100">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={"text-left h-[50px] text-black text-[14px] border-r-1"}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows?.length ?
              table.getRowModel().rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  className={cn(
                    "border-g-200",
                    // index % 2 === 1 ? "bg-gray-100" : "",
                    onRowClick && "cursor-pointer"
                  )}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {row.getVisibleCells().map((cell, i) => {
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          "h-[45px]", 
                          row.depth == 0 ? "text-yellow-700 font-bold italic bg-gray-200" : row.depth == 1 ? "bg-[#FBEFD9] text-[12px] text-black italic" : "text-gray-500 text-[10px]",
                          !row.getCanExpand() ? 'border-r-1' : ""
                        )}
                      >
                        {i === 0 && row.getCanExpand() ? (
                          <div className="flex flex-row items-center gap-2" style={{ paddingLeft: index ==0 ? '0px' : `${row.depth * 20}px` }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                row.toggleExpanded();
                              }}
                            >
                              <ChevronDown
                                className={cn(
                                  "w-4 h-4 transition-transform",
                                  !row.getIsExpanded() && "-rotate-90"
                                )}
                              />
                            </button>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </div>
                        ) : 
                        <div style={{paddingLeft: i === 0 && !row.getCanExpand() ? `${row.depth * 25}px` : 0 }}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      }   
                      </TableCell>
                    )}
                  )}
                </TableRow>
              )) : <TableRow className="w-full">
              <TableCell colSpan={columns.length} className="py-10">
                <div className="flex flex-col items-center justify-center">
                  <img src={images.no_data} className="w-[200px] h-auto" />
                </div>
              </TableCell>
            </TableRow>
          }
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default DataTableCollapsibleRow;
